import { Controller } from "stimulus"
import { formatCurrencyAmount } from "../utils/format_currency_amount"

// Default matches the "Recommended" pill.
const DEFAULT_TIP_PCT = 10

// "Contribution to HLI's research" (Review step) — additive, on top of the donation amount.
// Hidden for portfolios that already allocate to the client's tip destination EIN.
// Lives on its own controller, separate from charity-split.
export default class extends Controller {
  static targets = ["pctInput", "customToggle", "customInputRow", "customInput", "hiddenField", "amountDisplay", "infoButton", "info"]

  static values = {
    variant: String,
    excludedPortfolioIds: Array
  }

  connect() {
    this.tipPct = DEFAULT_TIP_PCT
    this.refresh()
  }

  // The ⓘ beside the card's title reveals its explanation inline -- a title attribute tooltip never
  // shows on a click or a tap (and not at all on touch devices).
  toggleInfo() {
    if (!this.hasInfoTarget) return

    const expanded = this.infoTarget.classList.toggle("is-hidden") === false
    if (this.hasInfoButtonTarget) this.infoButtonTarget.setAttribute("aria-expanded", String(expanded))
  }

  get formRoot() {
    return this.element.closest("form")
  }

  // Reports the tip back to the Review step's own summary (Tip row / Total charge) -- this
  // controller is shared by every client's review screen (see _review.html.slim for both HLI and
  // OFTW), so it reaches the generic shared controller rather than a client-specific one.
  get sharedController() {
    return this.application.getControllerForElementAndIdentifier(this.formRoot, "donation-flow-shared")
  }

  get amountCents() {
    const input = this.formRoot?.querySelector('[name="donation_form[amount_cents]"]')
    return parseInt(input?.value, 10) || 0
  }

  get selectedPortfolioId() {
    return this.formRoot?.querySelector('[name="donation_form[managed_portfolio_id]"]')?.value
  }

  get excludedForSelectedPortfolio() {
    return this.excludedPortfolioIdsValue.includes(this.selectedPortfolioId)
  }

  get currencySymbolTarget() {
    return this.formRoot?.querySelector('[data-donation-flow-shared-target="amountCurrencySymbol"]')
  }

  get currencySymbol() {
    return this.currencySymbolTarget?.textContent || ""
  }

  // Stripe's zero-decimal currencies (JPY, KRW, VND, ...) have no minor unit
  get isZeroDecimalCurrency() {
    return this.currencySymbolTarget?.dataset.zeroDecimal === "true"
  }

  // Called by happier_lives_institute_donation_controller.js whenever the Review step is shown --
  // the donor may have picked a different amount since this card was last rendered.
  refresh() {
    this.render()
  }

  selectPct(event) {
    this.tipPct = Number(event.currentTarget.value) || 0
    if (this.hasCustomToggleTarget) this.customToggleTarget.checked = false
    if (this.hasCustomInputTarget) this.customInputTarget.value = ""
    if (this.hasCustomInputRowTarget) this.customInputRowTarget.classList.add("is-hidden")
    this.render()
  }

  toggleCustom() {
    if (!this.hasCustomToggleTarget) return

    if (this.customToggleTarget.checked) {
      this.pctInputTargets.forEach((radio) => { radio.checked = false })
      if (this.hasCustomInputRowTarget) this.customInputRowTarget.classList.remove("is-hidden")
      if (this.hasCustomInputTarget) this.customInputTarget.focus()
    } else {
      if (this.hasCustomInputRowTarget) this.customInputRowTarget.classList.add("is-hidden")
      if (this.hasCustomInputTarget) this.customInputTarget.value = ""
      const defaultRadio = this.pctInputTargets.find((radio) => Number(radio.value) === DEFAULT_TIP_PCT)
      if (defaultRadio) defaultRadio.checked = true
      this.tipPct = DEFAULT_TIP_PCT
    }
    this.render()
  }

  updateCustomPct() {
    if (!this.hasCustomInputTarget) return

    const raw = Number(this.customInputTarget.value)
    this.tipPct = Math.min(100, Math.max(0, Number.isNaN(raw) ? 0 : raw))
    this.render()
  }

  get tipCents() {
    if (this.excludedForSelectedPortfolio || this.amountCents <= 0) return 0

    return Math.round(this.amountCents * (this.tipPct / 100))
  }

  render() {
    const tipCents = this.tipCents
    const tipChanged = !this.hasHiddenFieldTarget || this.hiddenFieldTarget.value !== String(tipCents)

    this.element.classList.toggle("is-hidden", this.excludedForSelectedPortfolio)
    if (this.hasHiddenFieldTarget) this.hiddenFieldTarget.value = String(tipCents)
    if (this.hasAmountDisplayTarget) {
      const suffix = this.variantValue === "recurrent" ? "/month" : ""
      this.amountDisplayTarget.textContent = `${formatCurrencyAmount(tipCents, this.currencySymbol, this.isZeroDecimalCurrency)}${suffix}`
    }
    // Keeps the Review step's own "Tip" row / "Total charge" in sync with every pill/custom-input change.
    this.sharedController?.updateReviewSummaryCore()
    // Always notify listeners (OFTW re-applies "Charged today" on this event). tipChanged lets the
    // payment controller skip a no-op Stripe Customer+Intent re-setup.
    this.formRoot?.dispatchEvent(new CustomEvent("hli-tip:changed", {
      bubbles: true,
      detail: { tipChanged }
    }))
  }
}
